import React from "react";
import ReactECharts from "echarts-for-react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCalendar, FiLoader, FiRefreshCcw } from "react-icons/fi";

import { YEARS, formatDashboardTitle } from "./utils";
import { useCashInDashboard } from "./useCashInDashboard";
import SummaryCard from "./components/SummaryCard";
import ChartCard from "./components/ChartCard";
import CashInTable from "./components/CashInTable";

const EDACashInDashboardDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    selectedMonth, setSelectedMonth, months,
    selectedYear, setSelectedYear,
    dashboardData, loading, error,
    apiCurrentMonth,
    dashboard, selectedProcess,
    consolidatedReportLink,
    lineChartOption, barChart2Option, barChart3Option, donutChart4Option, chart4Legend
  } = useCashInDashboard(location);

  const title = formatDashboardTitle(dashboard?.eda_name);
  const selectedMonthLabel = months.find((m) => m.value === selectedMonth)?.label || "";

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
            <p className="mt-1 text-sm text-slate-500">
              Process: {formatDashboardTitle(selectedProcess)} | Status:{" "}
              {dashboard?.edaStatus || "Available"}
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>

        {!location.state ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Open this page from the dashboard list so the selected dashboard context is available.
          </div>
        ) : (
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">Year</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={loading}
                  className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {YEARS.map((y) => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">Month</span>
                <select
                  value={selectedMonth || ""}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  disabled={loading}
                  className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => setSelectedMonth(apiCurrentMonth)}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                <FiRefreshCcw className="h-3.5 w-3.5" />
                Reset to current month
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {loading && (
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                  <FiLoader className="h-4 w-4 animate-spin" />
                  Updating dashboard...
                </div>
              )}
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                <FiCalendar className="h-4 w-4" />
                Current period: {selectedMonthLabel} {selectedYear}
              </div>
            </div>
          </div>
        )}
      </div>

      {location.state && (
        <>
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {dashboardData.cards.map((card) => (
              <SummaryCard
                key={card.cardName}
                card={card}
                loading={loading}
                onClick={() => {
                  navigate("/Insights/eda-CashInsegment-detail", {
                    state: {
                      selectedProcess, dashboard,
                      segment: card.cardName, month: selectedMonth, year: selectedYear,
                    },
                  });
                }}
              />
            ))}
          </div>

          <ChartCard title="Monthly Cash In Trend – Onshore, Service & Advance" subtitle="Actuals Rs Cr across available months">
            <div className="mt-3 overflow-x-auto">
              <div className="min-w-[600px]">
                <ReactECharts option={lineChartOption} notMerge lazyUpdate showLoading={loading} style={{ height: 300, width: "100%" }} />
              </div>
            </div>
          </ChartCard>

          <div className="grid grid-cols-1 gap-5 2xl:grid-cols-3">
            <ChartCard title="Onshore — Project-wise" subtitle={`${selectedMonthLabel} ${selectedYear} · Rs Cr`}>
              <div className="mt-3">
                <ReactECharts option={barChart2Option} notMerge lazyUpdate showLoading={loading} style={{ height: 280, width: "100%" }} />
              </div>
            </ChartCard>

            <ChartCard title="Service — Group-wise" subtitle={`${selectedMonthLabel} ${selectedYear} · Rs Cr`}>
              <div className="mt-3">
                <ReactECharts option={barChart3Option} notMerge lazyUpdate showLoading={loading} style={{ height: 280, width: "100%" }} />
              </div>
            </ChartCard>

            <ChartCard title="Advance — Customer Split" subtitle={`${selectedMonthLabel} ${selectedYear} · Rs Cr`}>
              <div className="mt-3">
                <ReactECharts option={donutChart4Option} notMerge lazyUpdate showLoading={loading} style={{ height: 220, width: "100%" }} />
              </div>
              <div className="mt-3 space-y-2">
                {chart4Legend.map((item) => (
                  <div key={item.name} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1.5 text-sm last:border-b-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ backgroundColor: item.color }} />
                      <span className="truncate text-slate-500 font-bold" style={{ color: "#000", fontWeight: 700 }}>{item.name}</span>
                    </div>
                    <span className="font-mono text-slate-700 shrink-0" style={{ color: "#000", fontWeight: 700 }}>₹{Number(item.value || 0).toFixed(2)} Cr</span>
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>

          <CashInTable chart5={dashboardData.chart5} reportLink={consolidatedReportLink} />
        </>
      )}
    </div>
  );
};

export default EDACashInDashboardDetail;
